import { useSyncExternalStore } from 'react';

export type ResolvedTheme = 'dark' | 'light';
export type ThemeMode = 'system' | 'dark' | 'light';

const THEME_CYCLE: ThemeMode[] = ['system', 'dark', 'light'];

function getSystemTheme(): ResolvedTheme {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

function updateBodyClass(theme: ResolvedTheme) {
  if (typeof document !== 'undefined') {
    document.body.classList.remove('dark', 'light');
    document.body.classList.add(theme);
  }
}

function parseThemeMode(value: string | null): ThemeMode {
  if (value === 'dark' || value === 'light') {
    return value;
  }

  return 'system';
}

let systemTheme: ResolvedTheme = getSystemTheme();

let selectedTheme: ThemeMode =
  typeof localStorage !== 'undefined'
    ? parseThemeMode(localStorage.getItem('themeMode'))
    : 'system';

// A theme forced onto this document alone. It is never persisted, so it does
// not leak into other documents through storage: the page-editor preview
// iframe uses it to show a page in the other mode while the editor keeps its
// own.
let themeOverride: ResolvedTheme | null = null;

const listeners = new Set<() => void>();

export function getSelectedTheme(): ThemeMode {
  return selectedTheme;
}

export function getResolvedTheme(): ResolvedTheme {
  if (themeOverride !== null) {
    return themeOverride;
  }

  return selectedTheme === 'system' ? systemTheme : selectedTheme;
}

export function setThemeOverride(theme: ResolvedTheme | null) {
  if (themeOverride === theme) {
    return;
  }

  themeOverride = theme;
  emitThemeChange();
}

function emitThemeChange() {
  updateBodyClass(getResolvedTheme());
  listeners.forEach((listener) => listener());
}

export function setSelectedTheme(theme: ThemeMode | null) {
  const next = theme ?? 'system';

  if (selectedTheme === next) {
    return;
  }

  selectedTheme = next;

  if (typeof localStorage !== 'undefined') {
    if (next === 'system') {
      localStorage.removeItem('themeMode');
    } else {
      localStorage.setItem('themeMode', next);
    }
  }

  emitThemeChange();
}

export function toggleTheme() {
  const currentIndex = THEME_CYCLE.indexOf(selectedTheme);
  const nextIndex = (currentIndex + 1) % THEME_CYCLE.length;

  setSelectedTheme(THEME_CYCLE[nextIndex]);
}

export function subscribeToTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribeToTheme, getResolvedTheme, getResolvedTheme);
}

export function useSelectedTheme(): ThemeMode {
  return useSyncExternalStore(subscribeToTheme, getSelectedTheme, getSelectedTheme);
}

updateBodyClass(getResolvedTheme());

if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    systemTheme = e.matches ? 'dark' : 'light';
    emitThemeChange();
  });
}

// Theme state lives per document, so a toggle in another same-origin document
// — the editor chrome around the page-editor preview iframe, or another tab —
// only reaches this one through the storage event it fires. A null key means
// the whole storage was cleared.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== 'themeMode' && event.key !== null) {
      return;
    }

    const theme = event.key === null ? null : event.newValue;
    const next = parseThemeMode(theme);

    if (selectedTheme === next) {
      return;
    }

    selectedTheme = next;
    emitThemeChange();
  });
}
